import { FunctionComponent } from "react";
import update from "immutability-helper";
import isEmpty from "lodash/isEmpty";
import omit from "lodash/omit";
import set from "lodash/set";
import { makeVar, useReactiveVar } from "@apollo/client";

import config from "src/helpers/config";

interface StateValue {
    where: any;
    pagination: PaginationSetInput;
    reset?: boolean;
}

interface PaginationSetInput {
    limit: number;
    offset: number;
}

const initialValue = {
    where: {},
    pagination: {
        limit: config.PAGINATION_LIMIT,
        offset: 0,
    },
};
const stateVar = makeVar(initialValue);

export const withStateAndActionsForSet = (Component: FunctionComponent) => {
    const WithComponent = (props: any) => {
        const state = useReactiveVar(stateVar);

        function updateState({ where, pagination, reset = false }: StateValue) {
            const newState = update(
                state,
                reset
                    ? {
                          where: { $set: {} },
                          pagination: {
                              $set: {
                                  limit: config.PAGINATION_LIMIT,
                                  offset: 0,
                              },
                          },
                      }
                    : {
                          where: isEmpty(where)
                              ? { $set: where }
                              : {
                                    $merge: omit(where, "_and"),
                                    ...(where._and && {
                                        _and: {
                                            $set: where._and,
                                        },
                                    }),
                                },
                          pagination: { $merge: pagination },
                      },
            );

            stateVar(newState);
        }
        function onPaginationChange(pagination: PaginationSetInput) {
            updateState({ ...state, pagination });
        }
        function onStateReset() {
            updateState({ ...initialValue, reset: true });
        }

        function onNameChange(name: string) {
            const path = ["title", "_ilike"];
            const where = set({}, path, `%${name}%`);
            updateState({
                ...state,
                where,
                pagination: { ...state.pagination, offset: 0 },
            });
        }

        function onIdChange(id: number) {
            const path = ["id", "_eq"];
            const where = set({}, path, id);
            if (id)
                updateState({
                    ...state,
                    where,
                    pagination: { ...state.pagination, offset: 0 },
                });
            else
                updateState({
                    ...state,
                    where: {},
                    pagination: { ...state.pagination, offset: 0 },
                });
        }

        return (
            <Component
                {...{
                    // State
                    ...state,

                    // State Actions
                    onPaginationChange,
                    onStateReset,
                    onNameChange,
                    onIdChange,
                }}
                {...props}
            />
        );
    };
    return WithComponent;
};

export default {
    read() {
        return stateVar();
    },
};
